import { useState } from "react";
import { Play } from "lucide-react";

interface YouTubeFacadeProps {
  id: string;
  title: string;
  poster: string;
}

/**
 * Lightweight YouTube embed: shows a poster image + play button, and only loads
 * the (heavy, tracking) YouTube iframe once the user clicks. Uses the
 * privacy-friendly youtube-nocookie domain.
 */
export function YouTubeFacade({ id, title, poster }: YouTubeFacadeProps) {
  const [active, setActive] = useState(false);

  return (
    <div className="relative aspect-video overflow-hidden rounded-3xl bg-ink shadow-[0_20px_70px_-30px_rgba(0,0,0,0.5)]">
      {active ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 size-full"
        />
      ) : (
        <button
          type="button"
          onClick={() => setActive(true)}
          aria-label={`Play video: ${title}`}
          className="group absolute inset-0 size-full cursor-pointer"
        >
          <img
            src={poster}
            alt={title}
            className="size-full object-cover opacity-90 transition-opacity duration-300 group-hover:opacity-100"
          />
          <span className="absolute inset-0 bg-gradient-to-t from-ink/50 to-ink/10" />
          <span className="absolute top-1/2 left-1/2 flex size-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-brand text-white shadow-lg transition-transform duration-300 group-hover:scale-110">
            <Play className="size-8 translate-x-0.5 fill-current" />
          </span>
        </button>
      )}
    </div>
  );
}
